import { useEffect, useRef } from 'react';

// In local dev the React dev server runs on 9000 while the backend websockets are served from 8081.
const DEV_SERVER_PORT = '9000';
const BACKEND_PORT = '8081';

const MAX_RECONNECT_ATTEMPTS = 5;
const MAX_RECONNECT_DELAY_MS = 30000;

/**
 * Builds a websocket URL for the given path, switching the protocol to ws(s) and redirecting the
 * dev-server port to the backend port. Optional query params are appended.
 */
export function makeWebSocketUrl(path, params) {
    const url = new URL(path, window.location.href);
    url.protocol = url.protocol.replace('http', 'ws');
    url.port = url.port.replace(DEV_SERVER_PORT, BACKEND_PORT);
    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                url.searchParams.append(key, value);
            }
        });
    }
    return url.href;
}

/**
 * Opens a websocket to the given path and keeps it alive with exponential-backoff reconnection.
 * The socket (and any pending reconnect timer) is torn down on unmount or when the inputs change.
 *
 * @param path       the endpoint path, e.g. '/app/status'.
 * @param onMessage  handler invoked with each message event; always the latest closure.
 * @param options    { enabled = true, params } — set enabled to false to skip connecting, and pass
 *                   params to append query parameters (they are part of the reconnection identity).
 */
export function useReconnectingWebSocket(path, onMessage, { enabled = true, params } = {}) {
    // Keep the latest handler in a ref so the long-lived socket never invokes a stale closure.
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    const paramsKey = params ? JSON.stringify(params) : '';

    useEffect(() => {
        if (!enabled) return undefined;

        const effectiveParams = paramsKey ? JSON.parse(paramsKey) : undefined;

        let isMounted = true;
        let socket = null;
        let reconnectTimer = null;
        let attempts = 0;

        const connect = () => {
            socket = new WebSocket(makeWebSocketUrl(path, effectiveParams));

            socket.onopen = () => {
                attempts = 0;
            };

            socket.onmessage = (event) => {
                if (isMounted) onMessageRef.current(event);
            };

            socket.onerror = () => {
                socket.close();
            };

            socket.onclose = () => {
                if (!isMounted) return;
                if (attempts < MAX_RECONNECT_ATTEMPTS) {
                    const timeout = Math.min(1000 * (2 ** attempts), MAX_RECONNECT_DELAY_MS);
                    attempts += 1;
                    reconnectTimer = setTimeout(connect, timeout);
                } else {
                    console.error(`Max reconnect attempts reached for ${path}`);
                }
            };
        };

        connect();

        return () => {
            isMounted = false;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (socket) socket.close();
        };
    }, [path, enabled, paramsKey]);
}
